import React, {Component} from 'react';
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Table from "@amzn/meridian/table";
import TableRow from "@amzn/meridian/table/table-row";
import TableCell from "@amzn/meridian/table/table-cell";
import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Pagination from "@amzn/meridian/pagination";
import Tab, {TabGroup} from "@amzn/meridian/tab";
import {FormattedMessage} from "react-intl";
import {fetchReverseSortGroupPlan} from "../actions/ReverseSortGroupPlanActions";
import {getNodeId} from "../Utility";
import RefreshTimer from "./RefreshTimer";

const PAGE_SIZE = 25;

class ReverseSortGroupPlan extends Component {

    constructor(props) {
        super(props);
        this.state = {
            sortColumn: "sortGroup",
            sortDirection: "ascending",
            currentPage: 1,
            filterText: ""
        };
    }

    componentDidMount() {
        const nodeId = getNodeId();
        this.props.dispatch(fetchReverseSortGroupPlan(nodeId));
    }

    render() {
        const {loading, error, planDay, sortGroupPlans} = this.props;

        if (error) {
            return this.renderError(error);
        }

        if (loading && !sortGroupPlans.length) {
            return (
                <Column
                    height="400px"
                    alignmentHorizontal="center"
                >
                    <Loader/>
                </Column>
            );
        }

        if (planDay === null && sortGroupPlans.length === 0) {
            return (
                <Alert type="informational">
                    <FormattedMessage
                        id="reverseSortGroupPlan.noPlan"
                        defaultMessage="No reverse sort plan available for this station"
                    />
                </Alert>
            );
        }

        return (
            <Column spacing="medium">
                <TabGroup value="overview" fill="line">
                    <Tab value="overview">
                        <FormattedMessage id="Overview" />
                    </Tab>
                </TabGroup>
                <RefreshTimer deps={[sortGroupPlans]} />
                {this.renderContent()}
            </Column>
        );
    }

    renderContent() {
        const {planDay, sortGroupPlans} = this.props;
        const {filterText} = this.state;

        const filteredPlans = this.getFilteredPlans(sortGroupPlans);
        const sortedPlans = this.getSortedPlans(filteredPlans);
        const numberOfPages = Math.ceil(sortedPlans.length / PAGE_SIZE);
        const currentPage = Math.min(this.state.currentPage, numberOfPages || 1);
        const paginatedPlans = sortedPlans.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

        return (
            <React.Fragment>
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h300">
                        <FormattedMessage
                            id="reverseSortGroupPlan.planDay"
                            defaultMessage="Plan Day: {planDay}"
                            values={{planDay}}
                        />
                    </Text>
                    <Button onClick={() => this.handleDownloadCsv()}>
                        <FormattedMessage
                            id="reverseSortGroupPlan.downloadCsv"
                            defaultMessage="Download CSV"
                        />
                    </Button>
                </Row>
                <Column width="30%" minWidth="300px">
                    <Input type="text"
                           size="small"
                           placeholder="Filter by sort group ..."
                           prefix="Sort Group"
                           value={filterText}
                           onChange={value => this.handleFilterChange(value)}
                    />
                </Column>
                <Table headerRows={1} showDividers={true} showStripes={true} spacing="small"
                       fixHeaderRows={true} fixHeaderRowsFullWidth={true}
                       sortColumn={this.state.sortColumn}
                       sortDirection={this.state.sortDirection}
                       onSort={this.handleSort}>
                    {this.renderHeadings()}
                    {paginatedPlans.map((plan, index) => this.renderPlanRow(plan, index))}
                </Table>
                <Row alignmentVertical="top" alignmentHorizontal="center">
                    <Pagination numberOfPages={numberOfPages}
                                onChange={pageNumber => this.handlePageChange(pageNumber)}
                                currentPage={currentPage}
                    />
                </Row>
            </React.Fragment>
        );
    }

    handleFilterChange = (value) => {
        this.setState({filterText: value, currentPage: 1});
    }

    handlePageChange = (pageNumber) => {
        this.setState({currentPage: pageNumber});
    }

    handleSort = ({sortColumn, sortDirection}) => {
        this.setState({sortColumn, sortDirection});
    }

    getFilteredPlans(plans) {
        const {filterText} = this.state;
        if (!filterText || filterText.trim() === "") return plans;
        const lowerFilter = filterText.toLowerCase();
        return plans.filter(plan =>
            (plan.sortGroup || "").toLowerCase().includes(lowerFilter)
        );
    }

    getSortedPlans(plans) {
        const {sortColumn, sortDirection} = this.state;
        const sorted = [...plans].sort((a, b) => {
            let valA, valB;
            switch (sortColumn) {
                case "sortGroup":
                    valA = (a.sortGroup || "").toLowerCase();
                    valB = (b.sortGroup || "").toLowerCase();
                    return valA.localeCompare(valB);
                case "plannedPackageCount":
                    valA = a.plannedPackageCount || 0;
                    valB = b.plannedPackageCount || 0;
                    return valA - valB;
                case "locations":
                    valA = (a.locations || []).join(", ").toLowerCase();
                    valB = (b.locations || []).join(", ").toLowerCase();
                    return valA.localeCompare(valB);
                default:
                    return 0;
            }
        });
        if (sortDirection === "descending") {
            sorted.reverse();
        }
        return sorted;
    }

    renderHeadings() {
        return (
            <TableRow>
                <TableCell width="30%" sortColumn="sortGroup">
                    <FormattedMessage id="reverseSortGroupPlan.sortGroup" defaultMessage="Sort Group"/>
                </TableCell>
                <TableCell width="20%" alignmentHorizontal="center" sortColumn="plannedPackageCount">
                    <FormattedMessage id="reverseSortGroupPlan.plannedPackageCount" defaultMessage="Planned Package Count"/>
                </TableCell>
                <TableCell width="50%" alignmentHorizontal="center" sortColumn="locations">
                    <FormattedMessage id="reverseSortGroupPlan.locations" defaultMessage="Location(s)"/>
                </TableCell>
            </TableRow>
        );
    }

    renderPlanRow(plan, index) {
        return (
            <TableRow key={index} highlightOnHover={true}>
                <TableCell width="30%">{plan.sortGroup}</TableCell>
                <TableCell width="20%" alignmentHorizontal="center">{plan.plannedPackageCount}</TableCell>
                <TableCell width="50%" alignmentHorizontal="center">{plan.locations.join(", ")}</TableCell>
            </TableRow>
        );
    }

    handleDownloadCsv() {
        const {planDay, sortGroupPlans} = this.props;
        if (!sortGroupPlans || sortGroupPlans.length === 0) return;

        const headers = ["SortGroup", "PackageId", "Locations"];
        const rows = sortGroupPlans.flatMap(plan => {
            const locations = `"${plan.locations.join(", ")}"`;
            const packageIds = plan.packageIds || [];
            return packageIds.length > 0
                ? packageIds.map(id => [plan.sortGroup, id, locations].join(","))
                : [[plan.sortGroup, "", locations].join(",")];
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);

        const nodeId = getNodeId();
        const link = document.createElement("a");
        link.href = url;
        link.download = `ReverseSortGroupPlan-${nodeId}-${planDay || "unknown"}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    renderError(error) {
        switch (error.type) {
            case "RETRYABLE":
                return (
                    <Alert type="error">
                        <FormattedMessage
                            id="reverseSortGroupPlan.error.retryable"
                            defaultMessage="{message}"
                            values={{message: error.message}}
                        />
                        <Button onClick={() => this.handleRetry()}>
                            <FormattedMessage
                                id="reverseSortGroupPlan.error.retry"
                                defaultMessage="Retry"
                            />
                        </Button>
                    </Alert>
                );
            case "VALIDATION":
                return (
                    <Alert type="warning">
                        <FormattedMessage
                            id="reverseSortGroupPlan.error.validation"
                            defaultMessage="Invalid request parameters. Please check your station configuration."
                        />
                    </Alert>
                );
            case "NON_RETRYABLE":
                return (
                    <Alert type="error">
                        <FormattedMessage
                            id="reverseSortGroupPlan.error.nonRetryable"
                            defaultMessage="An error occurred while fetching the sort group plan. Please try again later."
                        />
                    </Alert>
                );
            default:
                return (
                    <Alert type="error">
                        <FormattedMessage
                            id="reverseSortGroupPlan.error.unknown"
                            defaultMessage="An unexpected error occurred."
                        />
                    </Alert>
                );
        }
    }

    handleRetry() {
        const nodeId = getNodeId();
        this.props.dispatch(fetchReverseSortGroupPlan(nodeId));
    }
}

const mapStateToProps = state => ({
    loading: state.reverseSortGroupPlanReducer.loading,
    error: state.reverseSortGroupPlanReducer.error,
    planDay: state.reverseSortGroupPlanReducer.planDay,
    sortGroupPlans: state.reverseSortGroupPlanReducer.sortGroupPlans
});

export default withRouter(connect(mapStateToProps)(ReverseSortGroupPlan));



// WEBPACK FOOTER //
// ./src/components/ReverseSortGroupPlan.js